import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import { OptimizedImage } from "~/components/OptimizedImage";
import { Link } from "~/components/RouterLink";
import type { LiveSeoContent as LiveSeoPageData } from "~/data/liveSeoContent.functions";
import styles from "../live-seo-page.module.css";

type TheDemolitionIsRealLocalization = {
	h1: string;
	date: string;
	paragraphs: string[];
	relatedTitle: string;
	relatedDate: string;
	relatedPostTitle: string;
};

const theDemolitionIsRealLocalization = {
	[Locale.EN]: {
		h1: "The Demolition is Real",
		date: "May 12, 2016",
		paragraphs: [
			"One month has officially passed since we started construction on our new office. The progress is exciting to watch even though “progress” right now means demolition of the old interior.",
			"I love HGTV. My husband actually got me hooked on Property Brothers a couple years ago. Sometimes the only way I can stay on the treadmill for longer than 15 minutes is when I’m watching Drew and Jonathan transform those fixer-uppers into beautiful homes. The results are always so amazing. One thing I don’t think they show enough of, though, is the demolition phase of the project—the scary time when you don’t know how all of that rubble and empty space is going to morph into a beautiful home. The show definitely makes it seem like these projects only take minutes to complete. It would be amazing if my construction could be done by the end of my workout. Unfortunately, it takes slightly longer. In the meantime, we are very fortunate to have access to my father’s endodontic office where I can treat patients while waiting for the construction to finish.",
			"We’re one month into our construction and I can’t wait to see the finished result!",
		],
		relatedTitle: "Also Worth Checking Out",
		relatedDate: "May 25, 2017",
		relatedPostTitle: "Smile… It’s the Best Medicine",
	},
	[Locale.ES]: {
		h1: "La demolición es real",
		date: "12 de mayo de 2016",
		paragraphs: [
			"Ya pasó oficialmente un mes desde que empezamos la construcción de nuestra nueva oficina. Es emocionante ver el progreso, aunque “progreso” en este momento signifique la demolición del interior anterior.",
			"Me encanta HGTV. De hecho, mi esposo me enganchó con Property Brothers hace un par de años. A veces, la única manera de quedarme en la caminadora por más de 15 minutos es viendo a Drew y Jonathan transformar esas casas para remodelar en hogares hermosos. Los resultados siempre son increíbles. Sin embargo, algo que creo que no muestran lo suficiente es la fase de demolición del proyecto: ese momento intimidante en el que no sabes cómo todos esos escombros y espacios vacíos se convertirán en una casa hermosa. El programa definitivamente hace que parezca que estos proyectos toman solo minutos. Sería increíble que mi construcción pudiera terminar al final de mi entrenamiento. Desafortunadamente, toma un poco más de tiempo. Mientras tanto, somos muy afortunados de tener acceso a la oficina de endodoncia de mi padre, donde puedo atender pacientes mientras esperamos que termine la construcción.",
			"Llevamos un mes de construcción y no puedo esperar a ver el resultado final.",
		],
		relatedTitle: "También vale la pena revisar",
		relatedDate: "25 de mayo de 2017",
		relatedPostTitle: "Sonríe… es la mejor medicina",
	},
} satisfies Record<Locale, TheDemolitionIsRealLocalization>;

export function TheDemolitionIsRealPage({ page }: { page: LiveSeoPageData }) {
	const getLocalization = useLocalization(theDemolitionIsRealLocalization);
	const paragraphs = getLocalization("paragraphs");

	return (
		<Flex direction="column">
			<article className={styles.articleBand}>
				<Box className={styles.articleInner}>
					<OptimizedImage
						src="/miami-ortho-office-4.jpg"
						alt="Modern orthodontic office interior with green accents, showcasing dental equipment and seating, emphasizing Miami orthodontics and the welcoming environment for braces and Invisalign consultations."
						width={page.image?.width}
						height={page.image?.height}
						className={styles.featuredImage}
						fetchPriority="high"
						sizes="(max-width: 767px) 92vw, 760px"
					/>
					<Heading as="h1" className={styles.postTitle}>
						{getLocalization("h1")}
					</Heading>
					<Text as="p" className={styles.postDate}>
						{getLocalization("date")}
					</Text>
					<Box className={styles.entryContent}>
						{paragraphs.map((paragraph) => (
							<Text as="p" size="4" key={paragraph}>
								{paragraph}
							</Text>
						))}
					</Box>
					<section className={styles.relatedPosts}>
						<Heading as="h3" className={styles.relatedTitle}>
							{getLocalization("relatedTitle")}
						</Heading>
						<Link to="/smile-best-medicine" className={styles.relatedCard}>
							<OptimizedImage
								src="/smile-best-medicine.png"
								alt="Smiling woman with curly hair showcasing a bright smile, emphasizing the importance of oral health and confidence in orthodontic treatment, relevant to Miami orthodontics and braces."
								className={styles.relatedImageWide}
								loading="lazy"
								sizes="(max-width: 767px) 65vw, 33vw"
							/>
							<Box className={styles.relatedContent}>
								<Text as="p" className={styles.postDate}>
									{getLocalization("relatedDate")}
								</Text>
								<Heading as="h3" className={styles.relatedPostTitle}>
									{getLocalization("relatedPostTitle")}
								</Heading>
							</Box>
						</Link>
					</section>
				</Box>
			</article>
		</Flex>
	);
}
